<template>
  <div class="kitchen-display">
    <header class="header">后厨催菜大屏</header>
    <div v-if="displayList.length === 0" class="empty-tip">暂无数据</div>
    <div v-else class="grid">
      <div v-for="(item, index) in displayList" :key="index" class="card">
        <div class="title">{{ item.tableNo }}</div>
        <div
          class="status"
          v-if="item.waitingMinutes > 0"
          :class="item.isRed ? 'red' : 'green'"
        >
          催菜: {{ item.waitingMinutes }}分钟
        </div>
      </div>
    </div>
    <div v-if="!wsConnected" class="reconnect-tip">
      连接已断开，正在重新连接...
    </div>
  </div>
</template>

<script>
export default {
  name: "KitchenDisplay",
  data() {
    return {
      storeId: 1,
      ws: null,
      wsConnected: false,
      reconnectTimer: null,
      displayList: [],
      speechQueue: [],
      isSpeaking: false,
    };
  },
  created() {
    // 从 URL 中获取 storeId
    // 支持形式：?storeId=1 或者 /path/1
    const urlParams = new URLSearchParams(window.location.search);
    const urlStoreId = urlParams.get("storeId");
    if (urlStoreId) {
      this.storeId = urlStoreId;
    } else {
      // 也支持从 hash / pathname 获取，如 #/1
      const pathMatch = window.location.pathname.match(/\/(\d+)/);
      if (pathMatch) this.storeId = pathMatch[1];
    }
    this.initWebSocket();
    window.addEventListener("beforeunload", this.closeWebSocket);
  },
  beforeDestroy() {
    this.closeWebSocket();
    window.removeEventListener("beforeunload", this.closeWebSocket);
  },
  methods: {
    initWebSocket() {
      const url = "wss://vip.jiyar.net/infra/ws";
      try {
        this.ws = new WebSocket(url);
      } catch (e) {
        console.error("WebSocket 创建失败", e);
        this.scheduleReconnect();
        return;
      }

      this.ws.onopen = () => {
        console.log("WebSocket 已连接: " + url);
        this.wsConnected = true;
        // 连接成功后发送订阅消息
        const subscribeMsg = {
          type: "KITCHEN_DISPLAY_SUBSCRIBE",
          content: JSON.stringify({ storeId: this.storeId }),
        };
        try {
          this.ws.send(JSON.stringify(subscribeMsg));
          console.log("已发送订阅消息:", subscribeMsg);
        } catch (e) {
          console.error("发送订阅消息失败", e);
        }
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event.data);
      };

      this.ws.onerror = (err) => {
        console.error("WebSocket 错误", err);
        this.wsConnected = false;
      };

      this.ws.onclose = () => {
        console.warn("WebSocket 已关闭");
        this.wsConnected = false;
        this.scheduleReconnect();
      };
    },
    handleMessage(data) {
      let msg;
      try {
        msg = typeof data === "string" ? JSON.parse(data) : data;
      } catch (e) {
        msg = data;
      }

      // 判断是否为广播播报消息，先提取播报文本
      let broadcastText = "";
      if (msg && msg.type === "KITCHEN_DISPLAY_BROADCAST") {
        const content =
          typeof msg.content === "string"
            ? JSON.parse(msg.content)
            : msg.content;
        broadcastText = content && content.text ? content.text : "";
        console.log("广播消息内容:", msg.content, "播报文本:", broadcastText);
      }

      let list = null;
      if (msg && typeof msg.content === "string") {
        try {
          list = JSON.parse(msg.content);
        } catch (e) {
          console.warn("content 字段解析失败", e);
        }
      }
      if (!Array.isArray(list) && Array.isArray(msg)) list = msg;
      if (!Array.isArray(list) && msg && Array.isArray(msg.list))
        list = msg.list;
      if (!Array.isArray(list) && msg && Array.isArray(msg.data))
        list = msg.data;
      // 新增：检查解析后的 content 对象中是否有 list 字段（处理 KITCHEN_DISPLAY_UPDATE 消息）
      if (!Array.isArray(list) && list && Array.isArray(list.list))
        list = list.list;

      // 如果有广播播报消息，先进行语音播报
      if (broadcastText) {
        this.speakText(broadcastText);
      }

      // 如果没有列表数据，直接返回
      if (!Array.isArray(list)) return;

      // 过滤空项 + 映射字段
      this.displayList = list
        .filter((item) => item && (item.tableNo || item.orderNo))
        .map((item) => {
          const tableNo = item.tableNo || item.orderNo || "";
          const waitingMinutes =
            typeof item.waitingMinutes === "number" ? item.waitingMinutes : 0;
          const isRed =
            (item.colorLevelName &&
              item.colorLevelName.toUpperCase() === "RED") ||
            item.colorLevel === 2;
          return { tableNo, waitingMinutes, isRed };
        });
      console.log(this.displayList);
    },
    speakText(text) {
      console.log("播报文本:", text);
      if (!("speechSynthesis" in window)) {
        console.warn("当前浏览器不支持语音播报");
        return;
      }
      this.speechQueue.push(text);
      this.processSpeechQueue();
    },
    processSpeechQueue() {
      if (this.isSpeaking || this.speechQueue.length === 0) return;
      this.isSpeaking = true;
      const text = this.speechQueue.shift();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = "zh-CN";
      utterance.rate = 1;
      utterance.pitch = 1;
      utterance.volume = 1;
      utterance.onend = () => {
        this.isSpeaking = false;
        this.processSpeechQueue();
      };
      utterance.onerror = (e) => {
        console.error("语音播报错误", e);
        this.isSpeaking = false;
        this.processSpeechQueue();
      };
      window.speechSynthesis.speak(utterance);
    },
    scheduleReconnect() {
      if (this.reconnectTimer) return;
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.initWebSocket();
      }, 3000);
    },
    closeWebSocket() {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      if (this.ws) {
        this.ws.onclose = null;
        this.ws.close();
        this.ws = null;
      }
      this.wsConnected = false;
    },
  },
};
</script>

<style scoped>
.kitchen-display {
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
  padding: 30px;
  background: #f4f4f4;
  color: #333;
}

.header {
  background: #ffffff;
  color: #ff8c3a;
  font-size: 28px;
  font-weight: bold;
  text-align: center;
  padding: 18px 0;
  letter-spacing: 4px;
}

.empty-tip {
  margin: 60px auto;
  text-align: center;
  font-size: 20px;
  color: #999;
  padding: 60px 0;
  background: #ffffff;
  border-radius: 6px;
}

.grid {
  background: #ccc;
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
}

.card {
  background: #ffffff;
  border-radius: 6px;
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: 100px;
}

.title {
  text-align: center;
  font-size: 26px;
  font-weight: bold;
  padding: 0 0 10px 0;
  color: #222;
}

.status {
  color: #ffffff;
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  padding: 4px 0;
  border-radius: 4px;
  margin-top: auto;
}

.status.red {
  background: #e53935;
}
.status.green {
  background: #00a854;
}

.reconnect-tip {
  position: fixed;
  bottom: 16px;
  right: 16px;
  background: #e53935;
  color: #fff;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
}
</style>
